import * as React from 'react';

import logo from '@/assets/glytoucan_logo.png';
import { Head } from '@/components/seo';
import { PhaseBanner, RibbonBanner } from '@/components/ui/banner';
import { Footer } from '@/components/ui/footer';
import { paths } from '@/config/paths';
import { cn } from '@/utils/cn';
import { getPhase } from '@/utils/phase';

const FallbackNavbar = () => (
  <nav className="w-full overflow-hidden border-b bg-primary-800 drop-shadow-lg">
    <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
      <div className="flex h-16 items-center justify-between">
        <div className="flex grow items-center justify-center gap-12 md:shrink-0 md:justify-start">
          {/* GlyTouCan logo */}
          <a
            href={paths.home.getHref()}
            className="flex items-center gap-2 rounded-md pl-1 pr-2 hover:bg-primary-700"
          >
            <img alt="GlyTouCan" src={logo} className="h-12 w-auto" />
            <span className="text-xl font-bold text-white">GlyTouCan</span>
          </a>
        </div>
      </div>
    </div>
  </nav>
);

const FallbackHeader = () => {
  const phase = getPhase();
  return (
    <header className="sticky top-0 z-50">
      <div className="flex flex-col gap-1">
        <div className="z-10">
          <FallbackNavbar />
        </div>
        <PhaseBanner phase={phase} />
      </div>
      <div className="fixed right-0 top-0 z-10">
        <RibbonBanner phase={phase} />
      </div>
    </header>
  );
};

export type FallbackLayoutProps = {
  children: React.ReactNode;
};

const FallbackLayout = ({ children }: FallbackLayoutProps) => {
  return (
    <>
      <Head title="Something went wrong" />

      <div className="relative min-h-screen">
        {/* Background */}
        <div
          className={cn(['fixed', 'inset-0'])}
          style={{
            backgroundImage:
              "url('/top_structure_img.png'), url('/top_background.png')",
          }}
        />

        <div>
          className=
          {cn(
            ['bg-sub', 'bg-no-repeat', 'bg-left-top', 'bg-blend-lighten'],
            ['fixed', 'inset-0'],
          )}
        </div>

        {/* Overlay */}
        <div className={cn(['bg-white', 'opacity-30'], ['fixed', 'inset-0'])} />

        {/* Main contents */}
        <div className="relative flex min-h-screen flex-col">
          <FallbackHeader />
          <div className="flex flex-1 flex-col overflow-auto">
            <main
              className={cn(
                // Max width
                'container',
                // Centering
                'mx-auto',
                // Padding
                ['px-2', 'py-12'],
                // Expand main content area
                'grow',
              )}
            >
              {children}
            </main>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
};

FallbackLayout.displayName = 'FallbackLayout';

export { FallbackLayout };
